import React, { useState, lazy, Suspense, useMemo, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './vehicle-details.css';
import Footer from './components/Footer';

const VehicleList = lazy(() => import('./components/VehicleList'));
const VehicleDetails = lazy(() => import('./components/VehicleDetails'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const CustomerDashboard = lazy(() => import('./components/CustomerDashboard'));

function ConditionalFooter() {
  const location = useLocation();
  const hideFooterPaths = ['/vehicle/'];
  const shouldHideFooter = hideFooterPaths.some(path => location.pathname.includes(path));
  return shouldHideFooter ? null : <Footer />;
}

function App() {
  const [user, setUser] = useState(null);
  const [vehicles, setVehicles] = useState(() => {
    return [
        {id: 1, make: 'Toyota', model: 'Prius', year: 2019, price: 8500000, mileage: 45000, type: 'Hybrid', image: 'https://images.unsplash.com/photo-1617788138017-80ad40651399?w=200&h=150&fit=crop&auto=format&q=75', description: 'Fuel efficient hybrid vehicle, perfect for Colombo traffic.', status: 'available'},
        {id: 2, make: 'Suzuki', model: 'Alto', year: 2020, price: 3200000, mileage: 28000, type: 'Hatchback', image: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=200&h=150&fit=crop&auto=format&q=75', description: 'Compact and economical car, ideal for city driving.', status: 'available'},
        {id: 3, make: 'Honda', model: 'Vezel', year: 2018, price: 6800000, mileage: 52000, type: 'SUV', image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=200&h=150&fit=crop&auto=format&q=75', description: 'Popular crossover SUV with excellent resale value.', status: 'available'},
        {id: 4, make: 'Nissan', model: 'Leaf', year: 2017, price: 4500000, mileage: 38000, type: 'Electric', image: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=200&h=150&fit=crop&auto=format&q=75', description: 'Electric vehicle with low running costs, imported from Japan.', status: 'available'},
        {id: 5, make: 'Mitsubishi', model: 'Outlander', year: 2019, price: 7200000, mileage: 42000, type: 'SUV', image: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=200&h=150&fit=crop&auto=format&q=75', description: '7-seater family SUV with excellent safety features.', status: 'available'},
        {id: 6, make: 'BMW', model: 'X3', year: 2020, price: 12500000, mileage: 35000, type: 'SUV', image: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=200&h=150&fit=crop&auto=format&q=75', description: 'Luxury SUV with premium features and performance.', status: 'available'},
        {id: 7, make: 'Mercedes-Benz', model: 'C-Class', year: 2021, price: 15000000, mileage: 25000, type: 'Sedan', image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=200&h=150&fit=crop&auto=format&q=75', description: 'Executive sedan with advanced technology and comfort.', status: 'available'},
        {id: 8, make: 'Audi', model: 'A4', year: 2019, price: 11800000, mileage: 40000, type: 'Sedan', image: 'https://images.unsplash.com/photo-1617788138017-80ad40651399?w=200&h=150&fit=crop&auto=format&q=75', description: 'Sporty sedan with quattro all-wheel drive system.', status: 'available'},
        {id: 9, make: 'Tesla', model: 'Model 3', year: 2022, price: 18500000, mileage: 15000, type: 'Electric', image: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=200&h=150&fit=crop&auto=format&q=75', description: 'Premium electric sedan with autopilot features.', status: 'available'},
        {id: 10, make: 'Mazda', model: 'CX-5', year: 2020, price: 7800000, mileage: 32000, type: 'SUV', image: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=200&h=150&fit=crop&auto=format&q=75', description: 'Stylish crossover SUV with excellent fuel economy.', status: 'available'}
      ];
  });
  
  const [inquiries, setInquiries] = useState(() => {
    try {
      const saved = localStorage.getItem('inquiries');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [savedVehicles, setSavedVehicles] = useState(() => {
    try {
      const saved = localStorage.getItem('savedVehicles');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const saveToStorage = useCallback((key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch {}
  }, []);

  const addVehicle = useCallback((vehicle) => {
    const newVehicles = [...vehicles, { ...vehicle, id: Date.now(), status: 'available' }];
    setVehicles(newVehicles);
    saveToStorage('vehicles', newVehicles);
  }, [vehicles, saveToStorage]);

  const updateVehicle = useCallback((id, updatedVehicle) => {
    const newVehicles = vehicles.map(v => v.id === id ? { ...v, ...updatedVehicle } : v);
    setVehicles(newVehicles);
    saveToStorage('vehicles', newVehicles);
  }, [vehicles, saveToStorage]);

  const deleteVehicle = useCallback((id) => {
    const newVehicles = vehicles.filter(v => v.id !== id);
    setVehicles(newVehicles);
    saveToStorage('vehicles', newVehicles);
  }, [vehicles, saveToStorage]);

  const addInquiry = useCallback((inquiry) => {
    const newInquiries = [...inquiries, { ...inquiry, id: Date.now(), date: new Date().toLocaleDateString() }];
    setInquiries(newInquiries);
    saveToStorage('inquiries', newInquiries);
  }, [inquiries, saveToStorage]);

  const addSavedVehicle = useCallback((vehicle) => {
    const newSaved = [...savedVehicles, { ...vehicle, savedDate: new Date().toLocaleDateString() }];
    setSavedVehicles(newSaved);
    saveToStorage('savedVehicles', newSaved);
  }, [savedVehicles, saveToStorage]);

  const removeSavedVehicle = useCallback((vehicleId) => {
    const newSaved = savedVehicles.filter(v => v.id !== vehicleId);
    setSavedVehicles(newSaved);
    saveToStorage('savedVehicles', newSaved);
  }, [savedVehicles, saveToStorage]);

  const logout = useCallback(() => {
    setUser(null);
  }, []);

  return (
    <Router basename={process.env.NODE_ENV === 'production' ? '/dream-vehicle-website' : ''}>
      <div className="App">
        <nav className="navbar">
          <Link to="/" className="logo">Dream Vehicle</Link>
          <button className="mobile-menu-toggle" onClick={() => document.querySelector('.nav-links').classList.toggle('mobile-open')}>
            ☰
          </button>
          <div className="nav-links">
            <Link to="/">Browse Vehicles</Link>
            {user ? (
              <>
                {user.role === 'admin' ? (
                  <Link to="/admin">Admin Panel</Link>
                ) : (
                  <Link to="/dashboard">Dashboard</Link>
                )}
                <button onClick={logout} className="logout-btn">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login">Login</Link>
                <Link to="/register">Register</Link>
              </>
            )}
          </div>
        </nav>

        <Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'50vh',fontSize:'1.2rem',color:'#666'}} aria-label="Loading content">⚡ Loading...</div>}>
          <Routes>
            <Route path="/" element={<VehicleList vehicles={vehicles} user={user} onInquiry={addInquiry} onAddSaved={addSavedVehicle} />} />
            <Route path="/vehicle/:id" element={<VehicleDetails vehicles={vehicles} user={user} onInquiry={addInquiry} onAddSaved={addSavedVehicle} />} />
            <Route path="/login" element={<Login setUser={setUser} />} />
            <Route path="/register" element={<Register setUser={setUser} />} />
            <Route path="/admin" element={user?.role === 'admin' ? <AdminPanel vehicles={vehicles} inquiries={inquiries} onAdd={addVehicle} onUpdate={updateVehicle} onDelete={deleteVehicle} /> : <Login setUser={setUser} />} />
            <Route path="/dashboard" element={user ? <CustomerDashboard user={user} inquiries={inquiries.filter(i => i.customerEmail === user.email)} savedVehicles={savedVehicles} onAddSaved={addSavedVehicle} onRemoveSaved={removeSavedVehicle} /> : <Login setUser={setUser} />} />
          </Routes>
        </Suspense>
        
        <ConditionalFooter />
      </div>
    </Router>
  );
}

export default App;